import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { HeartPulse, QrCode, ArrowLeft, Download, Search, Loader2, CheckCircle, Clock } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BraceletsPage() {
  const navigate = useNavigate();
  const [bracelets, setBracelets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }
    fetchBracelets();
  }, [navigate, filter]);

  const fetchBracelets = async () => {
    try {
      const token = localStorage.getItem("token");
      let url = `${API}/admin/bracelets?limit=100`;
      if (filter === "registered") url += "&registered=true";
      if (filter === "unregistered") url += "&registered=false";
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBracelets(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate("/login");
      } else {
        toast.error("Failed to load bracelets");
      }
    } finally {
      setLoading(false);
    }
  };

  const downloadQR = (bracelet) => {
    const link = document.createElement("a");
    link.href = `data:image/png;base64,${bracelet.qr_code_data}`;
    link.download = `${bracelet.bracelet_code}-qr.png`;
    link.click();
    toast.success(`Downloaded QR for ${bracelet.bracelet_code}`);
  };

  const filteredBracelets = bracelets.filter(b =>
    b.bracelet_code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="container-app flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <QrCode className="h-6 w-6 text-teal-600" />
              <span className="font-bold text-xl text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Bracelets
              </span>
            </div>
          </div>
          <Link to="/dashboard" className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_bracelet-alert/artifacts/suao9dvz_SFA%20Logo%20-%20long.png" 
              alt="Stress-Free Aging" 
              className="h-10 w-auto"
            />
          </Link>
        </div>
      </header>

      <main className="container-app py-8">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search bracelet code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-11"
              data-testid="search-input"
            />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-full sm:w-48 h-11" data-testid="filter-select">
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Bracelets</SelectItem>
              <SelectItem value="registered">Registered</SelectItem>
              <SelectItem value="unregistered">Unregistered</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Results count */}
        <p className="text-sm text-slate-500 mb-4">
          Showing {filteredBracelets.length} bracelet{filteredBracelets.length !== 1 ? 's' : ''}
        </p>

        {/* Bracelets Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredBracelets.map((bracelet) => (
            <Card key={bracelet.id} className="border-slate-200 card-hover" data-testid={`bracelet-card-${bracelet.bracelet_code}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-mono" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {bracelet.bracelet_code}
                  </CardTitle>
                  {bracelet.is_registered ? (
                    <Badge className="bg-teal-100 text-teal-700 hover:bg-teal-100">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Registered
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                      <Clock className="h-3 w-3 mr-1" />
                      Available
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  {/* QR Preview */}
                  <div className="w-20 h-20 bg-white border border-slate-200 rounded-lg p-1 flex-shrink-0">
                    <img
                      src={`data:image/png;base64,${bracelet.qr_code_data}`}
                      alt={`QR code for ${bracelet.bracelet_code}`}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  
                  <div className="flex-1 space-y-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => downloadQR(bracelet)}
                      data-testid={`download-qr-${bracelet.bracelet_code}`}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download QR
                    </Button>
                    
                    {bracelet.is_registered ? (
                      <Link to={`/emergency/${bracelet.bracelet_code}`}>
                        <Button size="sm" className="w-full bg-teal-600 hover:bg-teal-700">
                          View Profile
                        </Button>
                      </Link>
                    ) : (
                      <Link to={`/register-bracelet/${bracelet.bracelet_code}`}>
                        <Button size="sm" variant="secondary" className="w-full">
                          Register
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredBracelets.length === 0 && (
          <div className="text-center py-12">
            <QrCode className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No bracelets found</p>
          </div>
        )}
      </main>
    </div>
  );
}
